import * as React from "react";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router-dom";
import {Panel, Row, Col, Alert, Table, ListGroup, ListGroupItem} from "react-bootstrap";
import {FormGroup, FormControl, ControlLabel, Button, Checkbox} from "react-bootstrap";
import {size, first, range, map, isEmpty, isNull, isString, isUndefined} from "lodash";
import "react-dates/lib/css/_datepicker.css";
import "font-awesome/less/font-awesome.less";
import "hint.css/hint.css";
import * as moment from "moment";
import * as validator from "card-validator";

import {ApplicationState} from "../reducers";
import "./styles/hotels.less";
import {Order, OrderDiscount, PaymentInfo} from "../models/order";
import Cart from "./Cart";
import {Address, User} from "../models/user";
import {
    checkDiscountCode,
    HotelAction, updateDiscountCode, updateOrderStore, validateCompletedOrder,
    validationError,
    removeDiscountCode
} from "../actions/hotels";
import {ccImages, states} from "../enums";
import {valid, validNumber} from "card-validator";
import {Loading} from "./Loading";
import {PackageTotals} from "./PackageTotals";

interface Props extends RouteComponentProps<any> {
    updateOrderStore: (order: Order) => HotelAction;
    validateCompletedOrder: (order: Order) => Promise<HotelAction>;
    validationError: (msg: string) => HotelAction;
    updateDiscountCode: (code: string) => HotelAction;
    checkDiscountCode: (code: string) => Promise<HotelAction>;
    removeDiscountCode: (orderDiscount: OrderDiscount) => HotelAction;
    order: Order;
    loading: boolean;
    validationErrorMessage: string;
    errorMessage: string;
    newDiscountCode: string;
    newDiscountSuccess: boolean;
    discountCodeMessage: string;
    discountCodeLoading: boolean;
}

interface ValidationResults {
    numberValidation: validNumber;
    monthValidation: valid;
    yearValidation: valid;
    cvvValidation: valid;
    zipValidation: valid;
    userZipValidation: valid;
    emptyFields: {[name: string]: boolean};
}

class Checkout extends React.Component<Props> {

    componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any): void {
        if (nextProps.order && isEmpty(nextProps.order.tickets)) {
            this.props.history.push("/");
            return;
        }
    }

    public render(): JSX.Element {
        return <div className="hotel-listing-c">
            <div className="package-container">
                <div className="checkout">
                    {this.renderCart()}
                    {this.renderCustomerInfoForm()}
                    {this.renderPaymentInformation()}
                </div>
            </div>
        </div>;
    }

    public renderCart(): JSX.Element {
        if (!this.props.order) {
            return null;
        }
        return (
            <Panel bsStyle="primary" className="cart">
                <Panel.Heading>Your Order</Panel.Heading>
                <Panel.Body>
                    <Cart/>
                    <h3>Guests</h3>
                    {this.renderGuestInfo()}
                    <h3>Discount Codes</h3>
                    {this.renderDiscountCodes()}
                    <h3>Totals</h3>
                    <PackageTotals order={this.props.order}/>
                </Panel.Body>
            </Panel>
        );
    }

    private renderGuestInfo(): JSX.Element {
        if (!this.props.order) {
            return null;
        }
        return (
            <Table striped condensed>
                <thead>
                <tr>
                    <th>Guest</th>
                </tr>
                </thead>
                <tbody>
                {this.props.order.guests.map((guest, index) => (
                    <tr key={index}>
                        <td>{guest.title} {guest.firstName} {guest.middleName} {guest.lastName} {guest.suffix}</td>
                    </tr>
                ))}
                </tbody>
            </Table>
        );
    }

    private renderDiscountCodes(): JSX.Element {
        if (!this.props.order) {
            return null;
        }
        const {newDiscountCode, discountCodeMessage, discountCodeLoading, newDiscountSuccess} = this.props;
        const discounts = this.props.order.discounts;

        let discountMsg = null;
        if (discountCodeMessage) {
            discountMsg = <Alert bsStyle={newDiscountSuccess ? "success": "warning"}>{discountCodeMessage}</Alert>;
        }

        return (
            <div className="discounts">
                {discounts.length > 0 && <Row>
                    <Col md={6}>
                        <ListGroup>
                            <ListGroupItem active>Applied Discount Codes</ListGroupItem>
                            {discounts.map(discount =>
                                <ListGroupItem key={discount.discountId}>
                                    {discount.discountName}
                                    <i className="fa fa-remove list-button"
                                       onClick={() => this.props.removeDiscountCode(discount)} />
                                </ListGroupItem>
                            )}
                        </ListGroup>
                    </Col>
                </Row>}
                {discountCodeLoading ? <Loading label="Checking Code"/> :
                    <Row>
                        <Col md={6}>
                            {discountMsg}
                            {discounts.length === 0 && <FormGroup>
                                <ControlLabel>Apply a Discount Code to your Order</ControlLabel>
                                <FormControl type="text"
                                             className="discount-field"
                                             value={newDiscountCode}
                                             onChange={(e: any) => this.props.updateDiscountCode(e.target.value)}/>
                                <Button onClick={() => this.props.checkDiscountCode(newDiscountCode)}>Apply</Button>
                            </FormGroup>}
                        </Col>
                    </Row>
                }
            </div>
        );
    }

    public renderCustomerInfoForm(): JSX.Element {
        if (!this.props.order) {
            return null;
        }
        const order = this.props.order;
        const user = this.getNewUser();
        const shippingAddress = this.getAddress(order.shippingAddress);
        const billingAddress = this.getAddress(order.billingAddress);
        const shippingZipValidation = validator.postalCode(shippingAddress.zip);
        const billingZipValidation = validator.postalCode(billingAddress.zip);

        return (
            <Panel bsStyle="primary">
                <Panel.Heading>Mailing Information</Panel.Heading>
                <Panel.Body>
                    <Col xs={12}>
                        <Row>
                            <Col md={6}>
                                <FormGroup validationState={Checkout.isNotEmpty(user.firstName)}>
                                    <ControlLabel>First Name *</ControlLabel>
                                    <FormControl type="text"
                                                 value={user.firstName}
                                                 onChange={(e: any) =>
                                                     this.updateUser((u: User) => u.firstName = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup validationState={Checkout.isNotEmpty(user.lastName)}>
                                    <ControlLabel>Last Name *</ControlLabel>
                                    <FormControl type="text"
                                                 value={user.lastName}
                                                 onChange={(e: any) =>
                                                     this.updateUser((u: User) => u.lastName = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup validationState={Checkout.isNotEmpty(user.phoneNumber)}>
                                    <ControlLabel>Phone Number *</ControlLabel>
                                    <FormControl type="text"
                                                 value={user.phoneNumber}
                                                 onChange={(e: any) =>
                                                     this.updateUser((u: User) => u.phoneNumber = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup validationState={Checkout.isNotEmpty(user.email)}>
                                    <ControlLabel>Email Address *</ControlLabel>
                                    <FormControl type="text"
                                                 value={user.email}
                                                 onChange={(e: any) =>
                                                     this.updateUser((u: User) => {
                                                         u.email = e.target.value;
                                                     })} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <h4>Shipping Address</h4>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup validationState={Checkout.isNotEmpty(shippingAddress.address1)}>
                                    <ControlLabel>Address Line 1 *</ControlLabel>
                                    <FormControl type="text"
                                                 value={shippingAddress.address1}
                                                 onChange={(e: any) =>
                                                     this.updateShippingAddress((u: Address) => u.address1 = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup validationState={Checkout.isNotEmpty(shippingAddress.address2)}>
                                    <ControlLabel>Address Line 2</ControlLabel>
                                    <FormControl type="text"
                                                 value={shippingAddress.address2}
                                                 onChange={(e: any) =>
                                                     this.updateShippingAddress((u: Address) => u.address2 = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup validationState={Checkout.isNotEmpty(shippingAddress.city)}>
                                    <ControlLabel>City *</ControlLabel>
                                    <FormControl type="text"
                                                 value={shippingAddress.city}
                                                 onChange={(e: any) =>
                                                     this.updateShippingAddress((u: Address) => u.city = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup validationState={Checkout.isNotEmpty(shippingAddress.state)}>
                                    <ControlLabel>State *</ControlLabel>
                                    <FormControl componentClass="select"
                                                 value={shippingAddress.state}
                                                 onChange={(e: any) =>
                                                     this.updateShippingAddress((u: Address) => u.state = e.target.value)}>
                                        <option value="" key="--" />
                                        {map(states, state => <option value={state.code} key={state.code}>{state.name}</option>)}
                                        <option value="n/a" key="n/a">-- Outside US --</option>
                                    </FormControl>
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup validationState={Checkout.validatorToState(shippingAddress.zip, shippingZipValidation)}>
                                    <ControlLabel>Postal Code *</ControlLabel>
                                    <FormControl type="text"
                                                 value={shippingAddress.zip}
                                                 onChange={(e: any) =>
                                                     this.updateShippingAddress((u: Address) => u.zip = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup validationState={Checkout.isNotEmpty(shippingAddress.country)}>
                                    <ControlLabel>Country *</ControlLabel>
                                    <FormControl type="text"
                                                 value={shippingAddress.country}
                                                 onChange={(e: any) =>
                                                     this.updateShippingAddress((u: Address) => u.country = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <h4>
                                    Billing Address
                                </h4>
                                <FormGroup>
                                    <Checkbox checked={order.billingSameAsShipping || false}
                                              onChange={(e: any) => {
                                                  order.billingSameAsShipping = e.target.checked;
                                                  this.updateOrder(order);
                                              }}>
                                        Billing address is the same as the shipping address
                                    </Checkbox>
                                </FormGroup>

                            </Col>
                        </Row>
                        {order.billingSameAsShipping ? null : (
                            <div>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup validationState={Checkout.isNotEmpty(billingAddress.address1)}>
                                            <ControlLabel>Address Line 1 *</ControlLabel>
                                            <FormControl type="text"
                                                         value={billingAddress.address1}
                                                         onChange={(e: any) =>
                                                             this.updateBillingAddress((u: Address) => u.address1 = e.target.value)} />
                                            <FormControl.Feedback />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup validationState={Checkout.isNotEmpty(billingAddress.address2)}>
                                            <ControlLabel>Address Line 2</ControlLabel>
                                            <FormControl type="text"
                                                         value={billingAddress.address2}
                                                         onChange={(e: any) =>
                                                             this.updateBillingAddress((u: Address) => u.address2 = e.target.value)} />
                                            <FormControl.Feedback />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup validationState={Checkout.isNotEmpty(billingAddress.city)}>
                                            <ControlLabel>City *</ControlLabel>
                                            <FormControl type="text"
                                                         value={billingAddress.city}
                                                         onChange={(e: any) =>
                                                             this.updateBillingAddress((u: Address) => u.city = e.target.value)} />
                                            <FormControl.Feedback />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup validationState={Checkout.isNotEmpty(billingAddress.state)}>
                                            <ControlLabel>State *</ControlLabel>
                                            <FormControl componentClass="select"
                                                         value={billingAddress.state}
                                                         onChange={(e: any) =>
                                                             this.updateBillingAddress((u: Address) => u.state = e.target.value)}>
                                                <option value="" key="--" />
                                                {map(states, state => <option value={state.code} key={state.code}>{state.name}</option>)}
                                                <option value="n/a" key="n/a">-- Outside US --</option>
                                            </FormControl>
                                            <FormControl.Feedback />
                                        </FormGroup>
                                    </Col>
                                    <Col md={2}>
                                        <FormGroup validationState={Checkout.validatorToState(billingAddress.zip, billingZipValidation)}>
                                            <ControlLabel>Postal Code *</ControlLabel>
                                            <FormControl type="text"
                                                         value={billingAddress.zip}
                                                         onChange={(e: any) =>
                                                             this.updateBillingAddress((u: Address) => u.zip = e.target.value)} />
                                            <FormControl.Feedback />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup validationState={Checkout.isNotEmpty(billingAddress.country)}>
                                            <ControlLabel>Country *</ControlLabel>
                                            <FormControl type="text"
                                                         value={billingAddress.country}
                                                         onChange={(e: any) =>
                                                             this.updateBillingAddress((u: Address) => u.country = e.target.value)} />
                                            <FormControl.Feedback />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </Col>
                </Panel.Body>
            </Panel>
        );
    }

    private renderPaymentInformation(): JSX.Element {
        if (!this.props.order) {
            return null;
        }
        const info = this.getPaymentInfo();

        const {numberValidation,
               monthValidation,
               yearValidation,
               cvvValidation,
               zipValidation} = this.validateForms();

        let cardImg = null;
        let cardName = null;
        if (info.cardType) {
            cardName = <span className="cc-type">{info.cardType}</span>;
            cardImg = <img className="cc-image" src={Checkout.ccImageForValidatorType(info.cardType)} />;
        }

        return (
            <Panel bsStyle="primary">
                <Panel.Heading>Payment Information</Panel.Heading>
                <Panel.Body>
                    <Col xs={12}>
                        <Row>
                            <Col md={4}>
                                <FormGroup validationState={Checkout.isNotEmpty(info.cardFirstName)}>
                                    <ControlLabel>Card First Name *</ControlLabel>
                                    <FormControl type="text"
                                                 value={info.cardFirstName}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.cardFirstName = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup validationState={Checkout.isNotEmpty(info.cardLastName)}>
                                    <ControlLabel>Card Last Name *</ControlLabel>
                                    <FormControl type="text"
                                                 value={info.cardLastName}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.cardLastName = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup validationState={Checkout.validatorToState(info.ccNum, numberValidation)}>
                                    <ControlLabel>Card Number *</ControlLabel>
                                    <FormControl type="text"
                                                 value={info.ccNum}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => {
                                                         u.ccNum = e.target.value;
                                                         if (numberValidation.card && numberValidation.card.type) {
                                                             u.cardType = numberValidation.card.type;
                                                         }
                                                     })} />
                                    <FormControl.Feedback />
                                    <div style={{textAlign: "right", marginTop: "10px"}}>{cardName} {cardImg}</div>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={2}>
                                <FormGroup validationState={Checkout.validatorToState(info.cvv2, cvvValidation)}>
                                    <ControlLabel>Card ID / CVV2 *</ControlLabel>
                                    <FormControl type="text"
                                                 value={info.cvv2}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.cvv2 = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup validationState={Checkout.validatorToState(info.expMonth, monthValidation)}>
                                    <ControlLabel>Card Expiration Month *</ControlLabel>
                                    <FormControl componentClass="select"
                                                 value={info.expMonth}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.expMonth = e.target.value)}>
                                        <option value={null} />
                                        {map(range(1, 13), month =>
                                            <option value={month} key={month}>
                                                {moment("2000-" + month, "YYYY-M").format("MM - MMMM")}
                                            </option>)}
                                    </FormControl>
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup validationState={Checkout.validatorToState(info.expYear, yearValidation)}>
                                    <ControlLabel>Card Expiration Year *</ControlLabel>
                                    <FormControl componentClass="select"
                                                 value={info.expYear}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.expYear = e.target.value)}>
                                        <option value={""} />
                                        {map(range(0, 10), i => {
                                            const y = moment().add(i, "years");
                                            const year = y.year();
                                            return <option value={year} key={year}>{year}</option>;
                                        })}
                                    </FormControl>
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <ControlLabel>Card Type *</ControlLabel>
                                    <FormControl componentClass="select"
                                                 value={info.cardType}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.cardType = e.target.value)}>
                                        <option value={""} />
                                        {map(["visa", "master-card", "american-express", "discover"], ct => {
                                            return <option value={ct} key={ct}>{ct}</option>;
                                        })}
                                    </FormControl>
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup validationState={Checkout.validatorToState(info.zipCode, zipValidation)}>
                                    <ControlLabel>Billing Zip Code *</ControlLabel>
                                    <FormControl type="text"
                                                 value={info.zipCode}
                                                 onChange={(e: any) =>
                                                     this.updatePaymentInfo((u: PaymentInfo) => u.zipCode = e.target.value)} />
                                    <FormControl.Feedback />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} style={{textAlign: "center", marginTop: "20px"}}>
                                {(this.props.validationErrorMessage || this.props.errorMessage) && (
                                    <Alert bsStyle="danger">
                                        {this.props.validationErrorMessage}
                                        {this.props.errorMessage}
                                    </Alert>
                                )}
                                <Button bsStyle={"success"} bsSize="lg" onClick={() => this.validateAndUpdateOrder()}>
                                    Review Order
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Panel.Body>
            </Panel>
        );
    }

    private validateAndUpdateOrder() {
        const results = this.validateForms();
        if (!isEmpty(results.emptyFields)) {
            // return this.props.validationError("Please fill in all required fields");
        }

        if (! (/* results.numberValidation.isValid && */
               results.userZipValidation.isValid &&
               results.cvvValidation.isValid &&
               results.monthValidation.isValid &&
               results.yearValidation.isValid &&
               results.zipValidation.isValid)) {
            // return this.props.validationError("Some required fields are invalid");
        }

        this.props.validateCompletedOrder(this.props.order)
            .then(action => {
                if (!action.validationError) {
                    this.props.history.push("/review");
                }
            });
    }

    private static validatorToState(value: string | number, v: valid): "success" | "warning" | "error" {
        if (!value) {
            return null;
        }
        if (v.isValid) {
            return "success";
        }
        if (v.isPotentiallyValid) {
            return null;
        }
        return "error";
    }

    private static isNotEmpty(value: string | number): "success" | "warning" | "error" {
        if (value && size(value + "") > 0) {
            return "success";
        }
        return null;
    }

    private static ccImageForValidatorType(t: string): string {
        switch (t) {
            case "visa": return ccImages.visa;
            case "master-card": return ccImages.mastercard;
            case "american-express": return ccImages.amex;
            case "discover": return ccImages.discover;
            default: return null;
        }
    }

    private validateForms(): ValidationResults {
        const user = this.getNewUser();
        const order = this.props.order;
        const shippingAddress = this.getAddress(order.shippingAddress);
        const billingAddress = this.getAddress(order.billingAddress);
        const info = this.getPaymentInfo();
        const numberValidation = validator.number(info.ccNum);
        const emptyFields: {[name: string]: boolean} = {};
        const emptyCheck: (o: any, f: string) => void = (o, k) => {
            const v: any = o[k];
            if (isNull(v) || isUndefined(v) || (isEmpty(v) && isString(v))) {
                emptyFields[k] = true;
            }
        };

        map(["email", "firstName", "lastName", "phoneNumber"],
                k => emptyCheck(user, k));

        map(["address1", "city", "state", "zip"],
            k => emptyCheck(shippingAddress, k));

        if (!order.billingSameAsShipping) {
            map(["address1", "city", "state", "zip"],
                k => emptyCheck(billingAddress, k));
        }

        map(["cardFirstName", "cardLastName", "ccNum",
            "cardType", "expMonth", "expYear", "zipCode", "cvv2"],
            k => emptyCheck(info, k));

        return {
            numberValidation,
            emptyFields,
            monthValidation: validator.expirationMonth(info.expMonth + ""),
            yearValidation: validator.expirationYear(info.expYear + ""),
            cvvValidation: validator.cvv(info.cvv2, numberValidation.card && numberValidation.card.isAmex ? 4 : 3),
            zipValidation: validator.postalCode(info.zipCode),
            userZipValidation: validator.postalCode(shippingAddress.zip)
        };
    }

    private getNewUser(): User {
        let user = this.props.order.newUser;
        if (!user) {
            user = {
                addresses: []
            } as User;
        }
        return user;
    }

    private getAddress(maybeAddress: Address | null): Address {
        if (maybeAddress) {
            return maybeAddress;
        }
        return {
            country: "USA"
        } as Address;
    }

    private getPaymentInfo(): PaymentInfo {
        let info = this.props.order.paymentInfo;
        if (!info) {
            info = {} as PaymentInfo;
        }
        return info;
    }

    private updateUser(fn: (u: User) => void) {
        const user = this.getNewUser();
        fn(user);
        const order = this.props.order;
        order.newUser = user;
        this.props.updateOrderStore(order);
    }

    private updateShippingAddress(fn: (a: Address) => void) {
        const order = this.props.order;
        const address = this.getAddress(order.shippingAddress);
        fn(address);
        order.shippingAddress = address;
        this.props.updateOrderStore(order);
    }

    private updateBillingAddress(fn: (a: Address) => void) {
        const order = this.props.order;
        const address = this.getAddress(order.billingAddress);
        fn(address);
        order.billingAddress = address;
        this.props.updateOrderStore(order);
    }

    private updateOrder(order: Order) {
        this.props.updateOrderStore(order);
    }

    private updatePaymentInfo(fn: (i: PaymentInfo) => void) {
        const info = this.getPaymentInfo();
        fn(info);
        const order = this.props.order;
        order.paymentInfo = info;
        this.props.updateOrderStore(order);
    }
}

const connected = connect((state: ApplicationState) => ({
    order: state.hotels.order,
    validationErrorMessage: state.hotels.validationError,
    errorMessage: state.hotels.errorMessage,
    loading: state.hotels.orderLoading || state.hotels.loading || state.hotels.searchLoading,
    newDiscountCode: state.hotels.newDiscountCode,
    discountCodeMessage: state.hotels.discountCodeMessage,
    discountCodeLoading: state.hotels.discountCodeLoading,
    newDiscountSuccess: state.hotels.newDiscountSuccess
}), {
    updateOrderStore,
    validateCompletedOrder,
    validationError,
    checkDiscountCode,
    updateDiscountCode,
    removeDiscountCode
})(Checkout);

export default withRouter<any>(connected);



// WEBPACK FOOTER //
// ./src/components/Checkout.tsx